import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import React, { useCallback, useState } from 'react';

import { AsyncFilterOptionList, useAsyncFilterOptions } from '@sophocles/data-grids/components/filters';
import { NoOptions, OptionCount, OptionLabel } from '@sophocles/data-grids/components/filters/FilterOptionRow';
import { OptionMark } from '@sophocles/data-grids/grid/descriptorIcons';

import { splitOptionCount } from './filterOptionLabel';
import FilterStaffChip from './FilterStaffChip';

/**
 * Right-hand panel inside the FilterBar popover.
 * Shows operator/value controls appropriate to the filter type.
 *
 * filter.type: 'single' | 'multi' | 'tree' | 'boolean' | 'daterange' | 'async'
 */
export default function FilterValuePanel({ filter, onApply, onCancel }) {
  if (filter.type === 'async') {
    return <AsyncSelectPanel filter={filter} onApply={onApply} onCancel={onCancel} />;
  }

  if (filter.type === 'boolean') {
    return <BooleanPanel filter={filter} onApply={onApply} onCancel={onCancel} />;
  }

  if (filter.type === 'multi') {
    return <MultiSelectPanel filter={filter} onApply={onApply} onCancel={onCancel} />;
  }

  if (filter.type === 'tree') {
    return <TreeSelectPanel filter={filter} onApply={onApply} onCancel={onCancel} />;
  }

  if (filter.type === 'daterange') {
    return <DateRangePanel filter={filter} onApply={onApply} onCancel={onCancel} />;
  }

  return <SingleSelectPanel filter={filter} onApply={onApply} onCancel={onCancel} />;
}

function BooleanPanel({ filter, onApply, onCancel }) {
  const [draft, setDraft] = useState(filter.value ?? 'true');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelTitle filter={filter} />
      <Box sx={{ px: 2, py: 1, flex: 1 }}>
        <RadioGroup value={draft} onChange={e => setDraft(e.target.value)}>
          <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
          <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
        </RadioGroup>
      </Box>
      <Divider />
      <Stack direction="row" spacing={1} sx={{ p: 1.5, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={() => onApply(draft)}>
          Apply Filter
        </Button>
      </Stack>
    </Box>
  );
}

/**
 * A filter whose vocabulary lives on the server. Selecting applies immediately
 * — the list is a moving target the reader searched for, and holding a draft
 * across a re-query would let Apply commit a row that scrolled away.
 */
function AsyncSelectPanel({ filter, onApply }) {
  const state = useAsyncFilterOptions({
    active: true,
    value: filter.value,
    fetchPage: filter.fetchPage,
    resolveValue: filter.resolveValue,
    selectedLabel: filter.selectedLabel,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
        {filter.label}
      </Typography>
      <AsyncFilterOptionList
        state={state}
        value={filter.value}
        onSelect={onApply}
        allLabel={filter.allLabel ?? 'All'}
        searchPlaceholder={filter.searchPlaceholder}
        renderOption={filter.renderOption}
        maxHeight={240}
        autoFocus={false}
      />
    </Box>
  );
}

// Sentinel for the unset state — a filter's null value is a real choice (the
// chip renders it as `allLabel`, e.g. the site status filter's "Active"), so
// the panel offers it as a leading row rather than leaving it unreachable.
const UNSET_VALUE = '__filter_value_panel_unset__';

// The panel's heading: the filter's label, badged when only staff see it.
function PanelTitle({ filter }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center' }}
    >
      {filter.label}
      {filter.staffOnly ? <FilterStaffChip /> : null}
    </Typography>
  );
}

function SingleSelectPanel({ filter, onApply, onCancel }) {
  const [draft, setDraft] = useState(filter.value ?? UNSET_VALUE);

  const options = filter.options || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelTitle filter={filter} />
      <List dense disablePadding sx={{ flex: 1, overflow: 'auto', maxHeight: 200, py: 0.5 }}>
        <ListItemButton selected={draft === UNSET_VALUE} onClick={() => setDraft(UNSET_VALUE)} sx={{ px: 2, py: 0.5 }}>
          <Radio size="small" checked={draft === UNSET_VALUE} sx={{ p: 0.5, mr: 1 }} />
          <ListItemText primary={filter.allLabel ?? 'All'} slotProps={{ primary: { variant: 'body2' } }} />
        </ListItemButton>
        {options.map(opt => {
          const { label, count } = splitOptionCount(opt);
          return (
            <ListItemButton
              key={opt.value}
              selected={draft === opt.value}
              disabled={opt.disabled}
              onClick={() => setDraft(opt.value)}
              sx={{ px: 2, py: 0.5 }}
            >
              <Radio size="small" checked={draft === opt.value} sx={{ p: 0.5, mr: 1 }} />
              <OptionMark icon={opt.icon} tone={opt.tone} />
              <OptionLabel label={label} />
              <OptionCount count={count} />
            </ListItemButton>
          );
        })}
      </List>
      <Divider />
      <Stack direction="row" spacing={1} sx={{ p: 1.5, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={() => onApply(draft === UNSET_VALUE ? null : draft)}>
          Apply Filter
        </Button>
      </Stack>
    </Box>
  );
}

function DateRangePanel({ filter, onApply, onCancel }) {
  const [from, setFrom] = useState(filter.value?.from ?? null);
  const [to, setTo] = useState(filter.value?.to ?? null);

  const handleApply = useCallback(() => {
    if (!from && !to) {
      onApply(null);
    } else {
      onApply({ from, to });
    }
  }, [from, to, onApply]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelTitle filter={filter} />
      <Stack spacing={1.5} sx={{ px: 2, py: 1, flex: 1 }}>
        <DatePicker
          label="From"
          value={from}
          onChange={setFrom}
          maxDate={to ?? undefined}
          slotProps={{ textField: { size: 'small', fullWidth: true } }}
        />
        <DatePicker
          label="To"
          value={to}
          onChange={setTo}
          minDate={from ?? undefined}
          slotProps={{ textField: { size: 'small', fullWidth: true } }}
        />
      </Stack>
      <Divider />
      <Stack direction="row" spacing={1} sx={{ p: 1.5, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="small" variant="contained" disabled={!from && !to} onClick={handleApply}>
          Apply Filter
        </Button>
      </Stack>
    </Box>
  );
}

function MultiSelectPanel({ filter, onApply, onCancel }) {
  const [draft, setDraft] = useState(new Set(filter.value || []));

  const options = filter.options || [];

  const handleToggle = useCallback(value => {
    setDraft(prev => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelTitle filter={filter} />
      {options.length === 0 && <NoOptions label={filter.label} />}
      <List dense disablePadding sx={{ flex: 1, overflow: 'auto', maxHeight: 200, py: 0.5 }}>
        {options.map(opt => {
          const { label, count } = splitOptionCount(opt);
          return (
            <ListItemButton
              key={opt.value}
              disabled={opt.disabled}
              onClick={() => handleToggle(opt.value)}
              sx={{ px: 2, py: 0.25 }}
            >
              <Checkbox size="small" checked={draft.has(opt.value)} sx={{ p: 0.5, mr: 1 }} />
              <OptionMark icon={opt.icon} tone={opt.tone} />
              <OptionLabel label={label} />
              <OptionCount count={count} />
            </ListItemButton>
          );
        })}
      </List>
      <Divider />
      <Stack direction="row" spacing={1} sx={{ p: 1.5, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="small" variant="contained" disabled={draft.size === 0} onClick={() => onApply([...draft])}>
          Apply Filter
        </Button>
      </Stack>
    </Box>
  );
}

/**
 * Two-level checkbox tree: parent nodes (e.g. alarm categories) with child
 * nodes (e.g. individual alarms). The filter's value is the set of selected
 * CHILD values only — parents are a selection convenience, never emitted on
 * their own. Clicking a parent selects/deselects every one of its children;
 * a parent's checkbox reflects the resulting state (checked when all its
 * children are selected, indeterminate when some are, unchecked otherwise).
 *
 * filter.options shape: [{ value, label, children: [{ value, label }] }]
 */
function TreeSelectPanel({ filter, onApply, onCancel }) {
  const [draft, setDraft] = useState(new Set(filter.value || []));

  const nodes = filter.options || [];

  const toggleParent = useCallback(node => {
    const childValues = (node.children ?? []).map(child => child.value);
    setDraft(prev => {
      const next = new Set(prev);
      const allSelected = childValues.length > 0 && childValues.every(value => next.has(value));
      childValues.forEach(value => (allSelected ? next.delete(value) : next.add(value)));
      return next;
    });
  }, []);

  const toggleChild = useCallback(value => {
    setDraft(prev => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelTitle filter={filter} />
      <List dense disablePadding sx={{ flex: 1, overflow: 'auto', maxHeight: 280, py: 0.5 }}>
        {nodes.map(node => {
          const childValues = (node.children ?? []).map(child => child.value);
          const selectedCount = childValues.filter(value => draft.has(value)).length;
          const checked = childValues.length > 0 && selectedCount === childValues.length;
          const indeterminate = selectedCount > 0 && !checked;
          return (
            <Box key={node.value}>
              <ListItemButton onClick={() => toggleParent(node)} sx={{ px: 2, py: 0.25 }}>
                <Checkbox size="small" checked={checked} indeterminate={indeterminate} sx={{ p: 0.5, mr: 1 }} />
                <ListItemText primary={node.label} slotProps={{ primary: { variant: 'body2', fontWeight: 600 } }} />
              </ListItemButton>
              {(node.children ?? []).map(child => (
                <ListItemButton
                  key={child.value}
                  onClick={() => toggleChild(child.value)}
                  sx={{ pl: 4, pr: 2, py: 0.25 }}
                >
                  <Checkbox size="small" checked={draft.has(child.value)} sx={{ p: 0.5, mr: 1 }} />
                  <ListItemText primary={child.label} slotProps={{ primary: { variant: 'body2' } }} />
                </ListItemButton>
              ))}
            </Box>
          );
        })}
      </List>
      <Divider />
      <Stack direction="row" spacing={1} sx={{ p: 1.5, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={() => onApply([...draft])}>
          Apply Filter
        </Button>
      </Stack>
    </Box>
  );
}
